<template>
  <div class="team-dir">
    <div class="page-header">
      <div>
        <h1 class="page-title">{{ t('teamPage.title') }}</h1>
        <p class="page-sub">{{ t('teamPage.sub') }}</p>
      </div>
    </div>

    <div v-if="loading" class="spinner"></div>

    <template v-else>
      <!-- Developers -->
      <div class="seclabel">{{ t('teamPage.developers') }}</div>
      <div class="dev-grid">
        <div v-for="u in users" :key="u.id" class="dev-card">
          <div class="dev-avatar">{{ initials(u.name) }}</div>
          <div class="dev-info">
            <div class="dev-name">{{ u.name }}</div>
            <div class="dev-role">{{ u.role === 'manager' ? t('roles.manager') : t('roles.developer') }}</div>
          </div>
        </div>
      </div>

      <!-- Shared projects -->
      <div class="seclabel">{{ t('teamPage.projects') }}</div>
      <div v-if="sharedProjects.length === 0" class="empty-state">
        <p>{{ t('teamPage.noProjects') }}</p>
      </div>
      <div v-else class="proj-grid">
        <router-link
          v-for="p in sharedProjects"
          :key="p.id"
          :to="'/tasks?project_id=' + p.id"
          class="proj-card"
        >
          <span class="proj-dot" :style="{ background: p.color }"></span>
          <div class="proj-body">
            <div class="proj-name">{{ p.name }}</div>
            <div class="proj-meta">{{ p.task_count }} {{ t('teamPage.tasksCount') }}</div>
          </div>
        </router-link>
      </div>
    </template>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import api from '@/api'
import { useI18n } from '@/composables/useI18n'

export default {
  name: 'TeamDirectoryView',
  setup() {
    const { t }  = useI18n()
    const users    = ref([])
    const projects = ref([])
    const loading  = ref(true)

    const sharedProjects = computed(() => projects.value.filter(p => p.visibility !== 'private'))
    const initials = (name) => (name || '').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()

    onMounted(async () => {
      try {
        const [u, p] = await Promise.all([api.getUsers(), api.getProjects()])
        users.value    = u.data || []
        projects.value = p.data || []
      } catch {} finally {
        loading.value = false
      }
    })

    return { t, users, sharedProjects, initials, loading }
  },
}
</script>

<style scoped>
.page-sub { color: var(--muted); font-size: 13px; margin-top: 2px; }
.seclabel {
  font-family: var(--font-mono);
  font-size: 11px; letter-spacing: .12em; text-transform: uppercase;
  color: var(--faint);
  margin: 28px 0 14px;
  display: flex; align-items: center; gap: 12px;
}
.seclabel::after { content: ""; flex: 1; height: 1px; background: var(--border); }

.dev-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 12px;
}
.dev-card {
  display: flex; align-items: center; gap: 12px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 14px 16px;
}
.dev-avatar {
  width: 38px; height: 38px; border-radius: 8px;
  background: var(--accent); color: var(--on-accent);
  display: grid; place-items: center; font-weight: 600; font-size: 13px;
  flex-shrink: 0;
}
.dev-name { font-size: 14px; font-weight: 600; }
.dev-role { font-size: 12px; color: var(--muted); margin-top: 1px; }

.proj-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 12px;
}
.proj-card {
  display: flex; align-items: center; gap: 12px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 14px 16px;
  text-decoration: none; color: var(--text);
  transition: border-color .12s, background .12s;
}
.proj-card:hover { border-color: var(--accent); background: var(--accent-wash); }
.proj-dot { width: 12px; height: 12px; border-radius: 4px; flex-shrink: 0; }
.proj-name { font-size: 14px; font-weight: 600; }
.proj-meta { font-size: 12px; color: var(--muted); margin-top: 1px; }
</style>
